import ClearIcon from '@cointracker/assets/general/clear-icon.svg?react';
import ZoomIcon from '@cointracker/assets/general/zoom-icon.svg?react';
import classNames from 'clsx';
import { useCallback, useState, type ChangeEvent } from 'react';
import { isSafariBrowser } from '../../../utils';
import { BetaTag } from './BetaTag';
import {
  betaLabelWrapper,
  clearIcon,
  clearIconWithBetaTag,
  searchContainer,
  searchContainerWithBorderChange,
  searchIconContainer,
  searchIconContainerWithBetaTag,
  searchInput,
  zoomIcon,
  type SearchBarVariants,
} from './SearchBar.css';

export type SearchBarProps = {
  value?: string;
  onChange: (value: string | null) => void;
  onClear?: () => void;
  placeholder?: string;
  margin?: string;
  searchIconMargin?: string;
  height?: string;
  changeBorderColorOnFocus?: boolean;
  defaultValue?: string;
  autoFocus?: boolean;
  showBetaTag?: boolean;
  className?: string;
} & SearchBarVariants;

export const SearchBar = (props: SearchBarProps) => {
  const {
    value: controlledValue,
    margin,
    searchIconMargin,
    height,
    onClear,
    onChange,
    variant,
    placeholder,
    defaultValue,
    changeBorderColorOnFocus = true,
    showBetaTag = false,
    className,
  } = props;
  const [value, setValue] = useState(defaultValue || '');

  const onValueChange = useCallback(
    (newValue: string) => {
      if (!controlledValue) {
        setValue(newValue);
      }

      if (onChange) {
        onChange(newValue);
      }
    },
    [controlledValue, onChange],
  );

  const onClearClick = () => {
    onValueChange('');
    if (onClear) {
      onClear();
    }
  };

  const inputValue = controlledValue ?? value;

  return (
    <div
      className={classNames(
        searchContainer,
        className,
        changeBorderColorOnFocus
          ? searchContainerWithBorderChange
          : searchContainer,
      )}
      style={{ margin, height }}
    >
      {showBetaTag && (
        <div className={betaLabelWrapper}>
          <BetaTag />
        </div>
      )}
      <span
        className={classNames(
          showBetaTag ? searchIconContainerWithBetaTag : searchIconContainer,
        )}
        style={{ margin: searchIconMargin }}
      >
        <ZoomIcon className={zoomIcon} />
      </span>
      <input
        className={searchInput({ variant })}
        type="text"
        placeholder={placeholder || 'Search...'}
        value={inputValue}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onValueChange(e.target.value)
        }
        // There's an issue with safari and the Focus/DismissableLayer of Radix that will create jumps when using the
        // autofocus property inside a modal. There's some relevant info on Material that had a similar issue
        // https://github.com/mui/material-ui/issues/16740 and Radix discussions
        // https://github.com/radix-ui/primitives/issues/922#issuecomment-948366132
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={props.autoFocus && !isSafariBrowser()}
      />

      {inputValue && (
        <span>
          <ClearIcon
            className={classNames(
              showBetaTag ? clearIconWithBetaTag : clearIcon,
            )}
            onClick={onClearClick}
            style={{ margin: searchIconMargin }}
          />
        </span>
      )}
    </div>
  );
};
